import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import CommandCenter from "../../_components/commandCenter/CommandCenter";
import { QuickCreateModals } from "../../_layouts/navigation/components/QuickCreateModals";
import { useQuickCreateModals } from "../../_layouts/navigation/hooks/useQuickCreateModals";

const CommandCenterContext = createContext(null);

/**
 * Mounted once at the authed app root (PrivateRoute), not inside AppLayout/
 * SettingsLayout - those two swap in/out of each other when the user
 * crosses the app/settings boundary, which would unmount this and lose the
 * open/closed state mid-navigation.
 *
 * Owns its own `useQuickCreateModals`/`QuickCreateModals` pair, separate
 * from Navigation.jsx's own "+" quick-actions instance - both are just
 * local state driving the same stateless modal-switch component, so two
 * independent instances coexist with zero conflict.
 */
export const CommandCenterProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggle]);

  const {
    activeQuickCreate,
    closeQuickCreateModal,
    openCompanyModal,
    openProposalModal,
    openSubscriptionModal,
    openInvoiceModal,
  } = useQuickCreateModals();

  const value = useMemo(() => ({ isOpen, open, close, toggle }), [isOpen, open, close, toggle]);

  return (
    <CommandCenterContext.Provider value={value}>
      {children}
      <CommandCenter
        isOpen={isOpen}
        onClose={close}
        openCompanyModal={openCompanyModal}
        openProposalModal={openProposalModal}
        openSubscriptionModal={openSubscriptionModal}
        openInvoiceModal={openInvoiceModal}
      />
      <QuickCreateModals activeQuickCreate={activeQuickCreate} onClose={closeQuickCreateModal} />
    </CommandCenterContext.Provider>
  );
};

export const useCommandCenter = () => {
  const ctx = useContext(CommandCenterContext);
  if (!ctx) throw new Error("useCommandCenter must be used within a CommandCenterProvider");
  return ctx;
};
